import { ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { FormRow, Text, Chip, StatusSwitcherVariants, usePopoverMenu } from '@frontend/design-system';
import { ChipRightElement } from '../../common';

interface TaskFormRowProps {
  children?: ReactNode;
  label: string;
  icon: IconName;
  placeholder?: string;
  buttonIconName: IconName;
  value: string;
  getTriggerProps?: ReturnType<typeof usePopoverMenu<HTMLElement>>['getTriggerProps'];
  disabled?: boolean;
  variant?: StatusSwitcherVariants;
  isEditing?: boolean;
  leftElement?: React.ReactNode;
  // Keeping this type here because Chip component does not have a defined type for rightElement
  rightElement?: React.ReactNode;
  chipCss?: Interpolation<Theme>;
  onClickRemove?: () => void;
  isRemoveable?: boolean;
  trackingIds?: {
    chipButton?: string;
  };
  isButton?: boolean;
}

export const TaskFormRow = ({
  children,
  label,
  icon,
  placeholder = '',
  buttonIconName,
  value,
  disabled = false,
  variant = 'default',
  trackingIds,
  leftElement,
  getTriggerProps,
  chipCss,
  onClickRemove,
  isRemoveable,
  isButton = true,
  ...rest
}: TaskFormRowProps) => {
  return (
    <FormRow css={rowStyle} cols={[35]}>
      <div css={labelContainerStyle}>
        <Icon name={icon} size={16} />
        <Text size='medium' weight='bold'>
          {label}
        </Text>
      </div>
      <Chip.StatusSwitcher
        disabled={disabled}
        variant={variant}
        leftElement={leftElement ?? <Icon name={buttonIconName} size={16} />}
        trackingId={trackingIds?.chipButton}
        css={[chipStyle, chipCss, !isButton && nonEditableChipStyle]}
        rightElement={
          <ChipRightElement
            isRemoveable={isRemoveable}
            onClickRemove={onClickRemove}
            value={value}
            showRightElement={isButton}
          />
        }
        {...getTriggerProps?.()}
        {...rest}
      >
        {value || placeholder}
      </Chip.StatusSwitcher>
      {children}
    </FormRow>
  );
};

const rowStyle = css`
  margin-right: 0;
  padding: 0;
  align-items: center;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
  min-height: 33px;
`;

const labelContainerStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  padding: ${theme.spacing(0.75, 0, 0.75, 0)};
`;

const chipStyle = css`
  max-width: 100%;
  justify-content: flex-start;
  padding: ${theme.spacing(0.75, 1, 0.75, 1)};
  height: 32px;

  span {
    font-size: ${theme.fontSize(14)};
  }

  :focus {
    outline: none;
  }
`;

const nonEditableChipStyle = css`
  :hover {
    cursor: default;
    outline: none;
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral60};
  }
  :focus {
    outline: none;
  }
`;
